"use client";

import { UserRole } from "@prisma/client";
import { useSession } from "next-auth/react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { TbHexagonPlusFilled, TbTemplate } from "react-icons/tb";

import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useChatSwitch } from "@/hooks/use-chat-switch";
import useInvite from "@/hooks/use-invite";
import { useLoggedUser } from "@/hooks/use-logged-user";
import { useProfileMenuDisplayName } from "@/hooks/use-profile-menu-display-name";
import { cn } from "@/lib/utils";

const SharedSidebar = () => {
  const { selectedView } = useChatSwitch();
  const nextView = selectedView === "chat" ? "split-view" : selectedView;
  const pathname = usePathname();
  const { orgId } = useLoggedUser();

  return (
    <div className="grid items-start gap-1">
      <Link
        href={`/${orgId}/project-templates?view=${nextView}`}
        className={cn(
          "group flex items-center rounded-md p-2 text-sm font-medium hover:bg-background/90",
          pathname?.startsWith(`/${orgId}/project-templates`) &&
            selectedView === "app" &&
            "bg-accent/70 font-semibold",
        )}
      >
        <TbTemplate className="mr-2 size-4" />
        Browse Templates
      </Link>
    </div>
  );
};

export const SharedSidebarFooter = () => {
  const { displayName, initials, email, orgId, user, isLoading } =
    useProfileMenuDisplayName();
  const { selectedView } = useChatSwitch();
  const nextView = selectedView === "chat" ? "split-view" : selectedView;
  const session = useSession();
  const { setInviteModalOpen } = useInvite();

  return (
    <div
      className="mb-14 flex w-full flex-col items-center"
      id="tour1-step3-admin"
    >
      {/* {(session.data?.user?.role === UserRole.SUPER_ADMIN ||
        session.data?.user?.role === UserRole.ADMIN) && (
        <Separator className="my-2" />
      )}
      <div className="w-full px-3">
        {session.data?.user?.role === UserRole.SUPER_ADMIN && (
          <Link
            href={`/sa/super?view=${nextView}`}
            className={cn(
              "group flex w-full items-center rounded-md px-3 py-2 text-sm font-medium hover:bg-background/90 hover:text-accent-foreground",
              pathname === `/sa/super` &&
                selectedView === "app" &&
                "bg-accent font-bold",
            )}
          >
            {pathname === `/sa/super` ? (
              <PiArmchairFill className="mr-2 size-4" />
            ) : (
              <PiArmchair className="mr-2 size-4" />
            )}
            Super admin
          </Link>
        )}
        {(session.data?.user?.role === UserRole.SUPER_ADMIN ||
          session.data?.user?.role === UserRole.ADMIN) && (
          <Link
            href={`/${orgId}/admin?view=${nextView}`}
            className={cn(
              "group flex w-full items-center rounded-md px-3 py-2 text-sm font-medium hover:bg-background/90 hover:text-accent-foreground",
              pathname === `/${orgId}/admin` &&
                selectedView === "app" &&
                "bg-accent font-bold",
            )}
          >
            {pathname === `/${orgId}/admin` ? (
              <MdAdminPanelSettings className="mr-2 size-4" />
            ) : (
              <MdOutlineAdminPanelSettings className="mr-2 size-4" />
            )}
            Admin
          </Link>
        )}
      </div> */}

      {/* moved invite to admin menu */}
      {/* {session.data?.user?.role === UserRole.SUPER_ADMIN ||
      session.data?.user?.role === UserRole.ADMIN ? (
        <>
          <Separator className="my-1" />
          <div className="flex">
            <Button
              variant="link"
              className="flex items-center justify-center"
              onClick={() => setInviteModalOpen(true)}
            >
              <TbHexagonPlusFilled className="mr-2" />
              Invite
            </Button>
            <Separator orientation="vertical" />
            <Button variant="link" asChild>
              <Link href="#" className="flex items-center justify-center">
                <LuShieldQuestion className="mr-2" />
                Help
              </Link>
            </Button>
          </div>
        </>
      ) : (
        <div className="mt-2" />
      )} */}
      {/* Bottom user menu (expanded: show name + email, click to sign out) */}
      <Separator className="my-2" />
      <div className="w-full px-3 pb-2">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              className="flex w-full items-center gap-2 rounded-md px-3 py-2 hover:bg-background/90"
            >
              {user ? (
                <Avatar className="size-8 shrink-0">
                  <AvatarFallback>{initials}</AvatarFallback>
                </Avatar>
              ) : (
                <div className="flex items-center justify-center">
                  <Skeleton className="size-8 shrink-0 rounded-full" />
                </div>
              )}
              <Tooltip>
                <TooltipTrigger asChild>
                  {isLoading ? (
                    <div className="min-w-0 flex-1">
                      <Skeleton className="h-4 w-24" />
                    </div>
                  ) : (
                    <span className="min-w-0 flex-1 truncate text-left text-sm font-medium">
                      {displayName}
                    </span>
                  )}
                </TooltipTrigger>
                <TooltipContent side="right">
                  <div className="flex flex-col text-sm">
                    {isLoading ? (
                      <>
                        <Skeleton className="mb-1 h-4 w-28" />
                        <Skeleton className="h-3 w-40" />
                      </>
                    ) : (
                      <>
                        <span className="font-medium">{displayName}</span>
                        {email && (
                          <span className="text-muted-foreground">{email}</span>
                        )}
                      </>
                    )}
                  </div>
                </TooltipContent>
              </Tooltip>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-56" side="right" align="start">
            {session.data?.user?.role === UserRole.SUPER_ADMIN && (
              <DropdownMenuItem asChild>
                <Link href="/sa/super" className="w-full cursor-pointer">
                  Super Admin
                </Link>
              </DropdownMenuItem>
            )}
            {orgId && (
              <DropdownMenuItem asChild>
                <Link
                  href={`/${orgId}/my-account?view=${nextView}`}
                  className="w-full cursor-pointer"
                >
                  My Account
                </Link>
              </DropdownMenuItem>
            )}
            {/* Subscriptions link moved to Admin menu */}
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link href="/auth/sign-out" className="w-full cursor-pointer">
                Sign out
              </Link>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <Separator className="my-2" />
      <div className="w-full px-3 pb-2">
        {(session.data?.user?.role === UserRole.SUPER_ADMIN ||
          session.data?.user?.role === UserRole.ADMIN) && (
          <div className="mb-2">
            <Button
              variant="ghost"
              className="group flex w-full items-center justify-start rounded-md p-2 text-left text-sm font-medium hover:bg-background/90 hover:text-accent-foreground"
              onClick={() => setInviteModalOpen(true)}
            >
              <TbHexagonPlusFilled className="size-4" />
              Invite
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SharedSidebar;
